import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Lock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { LEGAL } from "@/lib/brand";
import { toast } from "sonner";
import PaypalPay from "@/components/PaypalPay";
import StripePay from "@/components/StripePay";

const F = ({ label, ...p }) => (
  <label className="block">
    <span className="font-mono2 text-[10px] tracking-widest text-ape-muted">{label.toUpperCase()}</span>
    <input {...p} className="w-full bg-transparent border-b-2 border-ape-border rounded-none py-2 text-white focus:border-ape-red focus:outline-none focus:ring-0 transition-colors duration-150" />
  </label>
);

export default function Checkout() {
  const { items, total, clear, promo } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: user?.name || "", email: user?.email || "", address: "", city: "", state: "", zip: "", country: "USA", notes: "" });
  const [done, setDone] = useState(null);
  const [paid, setPaid] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  if (items.length === 0 && !done) { nav("/shop"); return null; }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/orders", { ...form, items: items.map((i) => ({ slug: i.slug, name: i.name, price: i.price, qty: i.qty })) });
      clear();
      setDone(data);
      toast.success("Order placed!");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-28 text-center">
        <CheckCircle2 className={`mx-auto mb-6 ${paid ? "text-green-500" : "text-ape-red"}`} size={64} />
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">{paid ? "PAYMENT COMPLETE" : "ORDER RESERVED"}</h1>
        {paid ? (
          <p className="text-ape-muted mt-4">Order <span className="font-mono2 text-white">{done.order_num}</span> is paid and confirmed. Your discreet shipment is being prepared. Stay relentless.</p>
        ) : (
          <p className="text-ape-muted mt-4">Order <span className="font-mono2 text-white">{done.order_num}</span> has been placed. Complete secure payment below with PayPal, or our team will reach out at <span className="text-white">{done.email}</span>.</p>
        )}
        <div className="mt-6 border border-ape-border bg-ape-surface p-6 text-left font-mono2 text-sm">
          <div className="flex justify-between"><span className="text-ape-muted">TOTAL</span><span className="text-ape-red font-bold">${done.total.toFixed(2)}</span></div>
          <div className="flex justify-between mt-1"><span className="text-ape-muted">STATUS</span><span data-testid="order-status">{paid ? "PAID" : "PENDING PAYMENT"}</span></div>
        </div>
        {!paid && (
          <div className="mt-8 text-left space-y-4">
            <div className="font-mono2 text-[10px] tracking-widest text-ape-muted text-center">CHOOSE YOUR PAYMENT METHOD</div>
            <StripePay orderNum={done.order_num} promoCode={promo?.code} />
            <div className="flex items-center gap-3 text-ape-muted">
              <div className="flex-1 h-px bg-ape-border" /><span className="font-mono2 text-[10px] tracking-widest">OR</span><div className="flex-1 h-px bg-ape-border" />
            </div>
            <PaypalPay orderNum={done.order_num} total={done.total} onPaid={() => setPaid(true)} />
          </div>
        )}
        <button onClick={() => nav("/account")} className="mt-8 bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest px-8 py-4 transition-colors duration-150" data-testid="view-orders-btn">View My Orders</button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-3 gap-10">
      <form id="checkout-form" onSubmit={submit} className="lg:col-span-2 space-y-6" data-testid="checkout-form">
        <h1 className="font-display text-4xl tracking-tight">CHECKOUT</h1>
        <div className="grid sm:grid-cols-2 gap-5">
          <F label="Full Name" required value={form.full_name} onChange={set("full_name")} data-testid="co-name" />
          <F label="Email" type="email" required value={form.email} onChange={set("email")} data-testid="co-email" />
          <div className="sm:col-span-2"><F label="Street Address" required value={form.address} onChange={set("address")} data-testid="co-address" /></div>
          <F label="City" required value={form.city} onChange={set("city")} data-testid="co-city" />
          <F label="State / Region" required value={form.state} onChange={set("state")} data-testid="co-state" />
          <F label="ZIP / Postal Code" required value={form.zip} onChange={set("zip")} data-testid="co-zip" />
          <F label="Country" required value={form.country} onChange={set("country")} data-testid="co-country" />
          <div className="sm:col-span-2"><F label="Order Notes (optional)" value={form.notes} onChange={set("notes")} data-testid="co-notes" /></div>
        </div>
        <div className="flex items-center gap-2 text-ape-muted text-sm"><Lock size={16} className="text-ape-red" /> Payment is not collected online yet. We finalize payment & shipping directly with you.</div>
      </form>

      <div className="border border-ape-border bg-ape-surface p-6 h-fit">
        <h2 className="font-display text-2xl tracking-wide mb-5">SUMMARY</h2>
        <div className="space-y-3 mb-4">
          {items.map((i) => (
            <div key={i.slug} className="flex justify-between text-sm">
              <span className="text-ape-muted">{i.name} × {i.qty}</span>
              <span className="font-mono2">${(i.price * i.qty).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-ape-border pt-4 flex justify-between items-baseline">
          <span className="font-head uppercase tracking-wide">Total</span>
          <span className="font-mono2 text-2xl font-bold text-ape-red">${total.toFixed(2)}</span>
        </div>
        <button type="submit" form="checkout-form" disabled={loading} className="w-full mt-6 bg-ape-red hover:bg-ape-redhover disabled:opacity-50 text-white font-head uppercase tracking-widest py-4 transition-colors duration-150" data-testid="place-order-btn">
          {loading ? "Placing..." : "Place Order"}
        </button>
        <div className="mt-4 font-mono2 text-[10px] tracking-widest text-ape-muted text-center">{LEGAL}</div>
      </div>
    </div>
  );
}
